import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { QueryErrorState } from '@/components/admin/shared/QueryErrorState';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { resolveSeriesColor } from '@/theme/chartPalette';
import { useAssistantInsightsSummaryQuery } from '@/hooks/useAssistantInsights';
import { isKnownTopic, resolveTopicColor } from './topicPalette';
import { UsageBreakdownChart } from './UsageBreakdownChart';
import { UsageFilterBar } from './UsageFilterBar';
import { UsageFilterChips } from './UsageFilterChips';
import {
  readUsageFilterState,
  toInsightsFilters,
  writeUsageFilterState,
  type UsageFilterState,
} from './usageFilters';
import { UsageKpiTiles } from './UsageKpiTiles';
import { UsageRateCard } from './UsageRateCard';
import { UsageTrendChart } from './UsageTrendChart';
import { UsageWorkspaceTable } from './UsageWorkspaceTable';
import { useUsageLabels } from './useUsageLabels';

const SCREEN_BAR_COLOR_INDEX = 1;

export function UsageInsightsSection() {
  const { t } = useTranslation('assistant');
  const { topicLabel, screenLabel } = useUsageLabels();
  const [searchParameters, setSearchParameters] = useSearchParams();
  const today = useMemo(function captureToday() {
    return new Date();
  }, []);
  const state = readUsageFilterState(searchParameters);
  const filters = toInsightsFilters(state, today);
  const summaryQuery = useAssistantInsightsSummaryQuery(filters);
  const summary = summaryQuery.data;

  function updateState(nextState: UsageFilterState) {
    setSearchParameters(writeUsageFilterState(searchParameters, nextState), { replace: true });
  }

  if (summary === undefined) {
    return summaryQuery.isError ? (
      <QueryErrorState
        onRetry={function retry() {
          void summaryQuery.refetch();
        }}
      />
    ) : (
      <div className="py-16 flex justify-center">
        <LoadingSpinner className="w-7 h-7" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <UsageFilterBar
        state={state}
        screens={summary.byScreen}
        today={today}
        onChange={updateState}
      />
      <UsageFilterChips state={state} onChange={updateState} />
      <div
        className={summaryQuery.isFetching ? 'opacity-70 transition-opacity' : 'transition-opacity'}
        aria-busy={summaryQuery.isFetching}
      >
        <div className="space-y-5">
          <UsageKpiTiles totals={summary.totals} />
          <UsageTrendChart
            points={summary.daily}
            isLoading={false}
            isError={false}
            onRetry={function retry() {
              void summaryQuery.refetch();
            }}
            onSelectDay={function zoomIntoDay(isoDate) {
              updateState({ ...state, period: 'custom', customFrom: isoDate, customTo: isoDate });
            }}
          />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <UsageBreakdownChart
              title={t('insights.topics.title')}
              hint={t('insights.topics.hint')}
              iconName="PieChart"
              items={summary.byTopic}
              selectedKey={state.topic}
              isLoading={false}
              isError={false}
              onRetry={function retry() {
                void summaryQuery.refetch();
              }}
              labelFor={topicLabel}
              colorFor={resolveTopicColor}
              onSelect={function selectTopic(topic) {
                updateState({
                  ...state,
                  topic: topic !== null && isKnownTopic(topic) ? topic : null,
                });
              }}
            />
            <UsageBreakdownChart
              title={t('insights.screens.title')}
              hint={t('insights.screens.hint')}
              iconName="LayoutDashboard"
              items={summary.byScreen}
              selectedKey={state.screen}
              isLoading={false}
              isError={false}
              onRetry={function retry() {
                void summaryQuery.refetch();
              }}
              labelFor={screenLabel}
              colorFor={function screenColor() {
                return resolveSeriesColor(SCREEN_BAR_COLOR_INDEX);
              }}
              onSelect={function selectScreen(screen) {
                updateState({ ...state, screen });
              }}
            />
          </div>
          <UsageWorkspaceTable
            filters={filters}
            selectedTenantId={state.tenantId}
            onSelect={function focusWorkspace(workspace) {
              updateState({
                ...state,
                tenantId: workspace === null ? null : workspace.tenantId,
                tenantName: workspace === null ? null : workspace.tenantName,
              });
            }}
          />
          <UsageRateCard rateCard={summary.rateCard} models={summary.byModel} />
        </div>
      </div>
    </div>
  );
}
